/**
 * Beweging in de full-screen variant van het blok "Uitgelichte afbeelding + content".
 *
 * Tijdens het scrollen zakt de achtergrond mee op ongeveer de halve snelheid van de
 * pagina en dimt hij een beetje weg; de tekst loopt juist iets vooruit. Alleen dat
 * verschil zie je -- bewust klein gehouden, de tekst blijft de hele tijd leesbaar. Bij
 * binnenkomst komt de kop regel voor regel van onder een masker vandaan.
 *
 * Loopt op dezelfde ScrollTrigger als de hero, dus via de Lenis-koppeling uit
 * smooth-scroll.js: één animatielus voor de hele pagina.
 *
 * Progressive enhancement: zonder JS -- of met prefers-reduced-motion -- staat alles
 * gewoon op zijn plek.
 *
 * Maten: resources/styles/common/featured-hero.scss.
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { minderBeweging, onthulRegels } from './hero-motion';

gsap.registerPlugin(ScrollTrigger);

export default function initFeaturedHero() {
  const secties = document.querySelectorAll('[data-featured-hero]');

  if (!secties.length || minderBeweging()) {
    return;
  }

  // De parallax hangt aan de achtergrondlaag, en die staat op mobiel niet meer absoluut
  // maar gewoon boven de tekst (zie de SCSS). Hem daar verschuiven zou een gat naast de
  // foto trekken. matchMedia zet de tijdlijn op en weer af bij het wisselen van formaat.
  const mm = gsap.matchMedia();

  secties.forEach((sectie) => {
    const achtergrond = sectie.querySelector('[data-featured-hero-bg]');
    const inhoud = sectie.querySelector('[data-featured-hero-content]');
    const kop = sectie.querySelector('[data-featured-hero-heading]');

    // De hele passage door beeld, van het moment dat de sectie onderin binnenkomt tot
    // hij er bovenaan uit loopt. In pixels en niet in procenten: de sectie is altijd
    // ongeveer een schermhoogte, dus procenten zouden op mobiel heel anders uitpakken.
    mm.add('(min-width: 768px)', () => {
      if (!achtergrond && !inhoud) {
        return;
      }

      const tijdlijn = gsap.timeline({
        scrollTrigger: {
          trigger: sectie,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });

      if (achtergrond) {
        // Van -60 naar 60 en niet van 0 naar 120: zo staat het beeld halverwege de
        // passage -- als de sectie het scherm vult -- precies op zijn plek.
        tijdlijn.fromTo(
          achtergrond,
          { y: -60 },
          { y: 60, ease: 'none' },
          0
        );

        // De dim loopt pas op de tweede helft: bij binnenkomst is het beeld op volle
        // sterkte, en hij verliest wat kleur terwijl hij eruit loopt.
        tijdlijn.fromTo(
          achtergrond,
          { opacity: 1 },
          { opacity: 0.65, ease: 'none' },
          0.5
        );
      }

      if (inhoud) {
        tijdlijn.fromTo(inhoud, { y: 40 }, { y: -40, ease: 'none' }, 0);
      }
    });

    // De kop komt regel voor regel van onder een masker vandaan zodra de sectie goed in
    // beeld staat. Het splitsen zelf staat in hero-motion.js, gedeeld met de andere hero's.
    onthulRegels(kop, {
      klasse: 'featured-hero__line',
      scrollTrigger: {
        trigger: sectie,
        start: 'top 65%',
        once: true,
      },
    });
  });
}
